import { useCallback, useEffect, useState } from 'react';
import {
  WITHDRAWAL_METHODS,
  WITHDRAWAL_MIN_AMOUNT,
  WITHDRAWAL_STATUS_META,
  listMyWithdrawals,
  requestWithdrawal,
  type WithdrawalMethod,
  type WithdrawalRequest,
} from '@/lib/withdrawals';

// ─────────────────────────────────────────────────────────────
// Retraits du portefeuille Zifek (côté commerce).
// Le commerce demande un retrait ; le SuperAdmin approuve ou refuse
// avec une explication. Le montant est bloqué dès la demande.
// ─────────────────────────────────────────────────────────────

interface Props {
  userId: number;
  currency: string;
  balance: number;
  onChanged: () => void;
}

function formatDate(value: string): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function WithdrawalPanel({ userId, currency, balance, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<WithdrawalMethod>('mobile_money');
  const [destination, setDestination] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [rows, setRows] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      setRows(await listMyWithdrawals(userId, 20));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  const selectedMethod = WITHDRAWAL_METHODS.find((m) => m.value === method) || WITHDRAWAL_METHODS[0];
  const pendingRow = rows.find((r) => r.status === 'pending');

  const submit = async () => {
    setError('');
    setNotice('');
    const value = Number(amount);
    setSubmitting(true);
    const result = await requestWithdrawal({
      userId,
      amount: value,
      currency,
      method,
      destination,
      note,
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error || 'Demande impossible.');
      return;
    }
    setNotice('Demande envoyée ! Le SuperAdmin va l’examiner et vous répondre.');
    setAmount('');
    setDestination('');
    setNote('');
    setOpen(false);
    await load();
    onChanged();
  };

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-b border-background-200/70">
        <i className="ri-bank-card-2-line text-primary-500"></i>
        <h3 className="text-sm font-semibold text-foreground-900">Retrait du solde</h3>
        <span className="ml-auto text-xs text-foreground-500 whitespace-nowrap">
          Disponible : <strong className="text-foreground-900">{balance.toLocaleString('fr-FR')} {currency}</strong>
        </span>
      </div>

      <div className="p-5">
        {notice && (
          <div className="mb-3 px-3 py-2.5 rounded-lg bg-accent-50 text-accent-700 border border-accent-200 text-xs flex items-start gap-2">
            <i className="ri-checkbox-circle-line mt-0.5"></i>
            <span className="flex-1">{notice}</span>
          </div>
        )}

        {pendingRow ? (
          <div className="mb-3 px-3 py-2.5 rounded-lg bg-secondary-50 border border-secondary-200/70 text-xs text-secondary-900 flex items-start gap-2">
            <i className="ri-time-line mt-0.5"></i>
            <span className="flex-1">
              Une demande de <strong>{pendingRow.amount.toLocaleString('fr-FR')} {pendingRow.currency}</strong> est en cours d’examen.
              Vous ne pouvez en créer une nouvelle qu’après la réponse du SuperAdmin.
            </span>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className={open ? 'ri-subtract-line' : 'ri-add-line'}></i>
            {open ? 'Annuler' : 'Demander un retrait'}
          </button>
        )}

        {open && !pendingRow && (
          <div className="mt-4 space-y-3 border-t border-background-200/70 pt-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Montant</label>
              <div className="relative">
                <input
                  type="number"
                  min={WITHDRAWAL_MIN_AMOUNT}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Minimum ${WITHDRAWAL_MIN_AMOUNT}`}
                  className="w-full px-3 py-2.5 pr-16 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-foreground-400">{currency}</span>
              </div>
            </div>

            <div>
              <span className="block text-xs font-semibold text-foreground-600 mb-1.5">Mode de retrait</span>
              <div className="grid grid-cols-3 gap-1.5">
                {WITHDRAWAL_METHODS.map((m) => (
                  <button
                    key={m.value}
                    type="button"
                    onClick={() => setMethod(m.value)}
                    className={`flex flex-col items-center gap-1 px-2 py-2.5 rounded-lg border text-[11px] font-medium transition-colors cursor-pointer ${
                      method === m.value
                        ? 'border-primary-300 bg-primary-50/60 text-primary-700'
                        : 'border-background-200/70 text-foreground-600 hover:border-background-300/60'
                    }`}
                  >
                    <i className={`${m.icon} text-base`}></i>
                    <span className="text-center leading-tight">{m.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">{selectedMethod.hint}</label>
              <input
                type="text"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder={selectedMethod.hint}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Note (facultatif)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                maxLength={300}
                className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 resize-none"
              ></textarea>
            </div>

            {error && (
              <div className="px-3 py-2.5 rounded-lg bg-red-50 text-red-600 text-xs flex items-start gap-2">
                <i className="ri-error-warning-line mt-0.5"></i>
                <span className="flex-1">{error}</span>
              </div>
            )}

            <button
              type="button"
              onClick={() => { void submit(); }}
              disabled={submitting}
              className="w-full py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              {submitting ? <><i className="ri-loader-4-line animate-spin"></i>Envoi…</> : <><i className="ri-send-plane-fill"></i>Envoyer la demande</>}
            </button>
            <p className="text-[11px] text-foreground-400 leading-relaxed">
              Le montant est bloqué sur votre portefeuille dès l’envoi. En cas de refus, il est automatiquement remboursé.
            </p>
          </div>
        )}

        {/* Historique */}
        <div className="mt-5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-foreground-400 mb-2">Mes demandes</p>
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-foreground-400 py-3">
              <i className="ri-loader-4-line animate-spin"></i> Chargement…
            </div>
          ) : rows.length === 0 ? (
            <p className="text-xs text-foreground-400 py-3">Aucune demande de retrait pour le moment.</p>
          ) : (
            <ul className="space-y-2">
              {rows.map((r) => {
                const meta = WITHDRAWAL_STATUS_META[r.status];
                return (
                  <li key={r.id} className="rounded-lg border border-background-200/70 p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-foreground-900 whitespace-nowrap">
                        {r.amount.toLocaleString('fr-FR')} {r.currency}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${meta.className}`}>
                        {meta.label}
                      </span>
                      <span className="ml-auto text-[11px] text-foreground-400 whitespace-nowrap">{formatDate(r.createdAt)}</span>
                    </div>
                    {r.destination && (
                      <p className="text-[11px] text-foreground-500 mt-1 truncate">
                        <i className="ri-arrow-right-s-line"></i> {r.destination}
                      </p>
                    )}
                    {r.adminNote && (
                      <p className={`text-[11px] mt-1 rounded-md px-2 py-1.5 ${
                        r.status === 'rejected' ? 'bg-red-50 text-red-600' : 'bg-background-100 text-foreground-600'
                      }`}>
                        <i className="ri-chat-1-line mr-1"></i>
                        {r.adminNote}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}